import React, { Fragment, useEffect, useState } from "react";
import Breadcrumb from "../common/breadcrumb";
import { ChevronDown, ChevronUp, RefreshCw } from "react-feather";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Badge,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import {
  getSaleOrderListing,
  updateSaleOrderStatus,
  syncTimeoutSaleOrders,
} from "../../utils/apiCalls";
import { useSelector } from "react-redux";

const statusColors = {
  pending: "warning",
  accepted: "info",
  packed: "primary",
  shipped: "primary",
  delivered: "success",
  cancelled: "danger",
};

const syncStatusColors = {
  pending: "secondary",
  synced: "success",
  failed: "danger",
  timeout: "dark",
};

const SaleOrderList = () => {
  const userType = localStorage.getItem("userType");
  const storedMerchantId = localStorage.getItem("MerchantId");
  const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
  const selectedMerchant = userType === "merchant" ? storedMerchantId : reduxSelectedMerchant;

  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Filters
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    merchantSyncStatus: "",
    orderId: "",
    merchantOrderId: "",
    search: "",
    minPrice: "",
    maxPrice: "",
    startDate: "",
    endDate: "",
  });
  const [tempFilters, setTempFilters] = useState(filters);

  // Status update modal
  const [statusModal, setStatusModal] = useState({ open: false, orderId: null, currentStatus: "" });
  const [newStatus, setNewStatus] = useState("");

  const fetchOrders = async () => {
    setLoading(true);
    const merchantId = selectedMerchant && selectedMerchant !== "all" ? selectedMerchant : undefined;
    const data = await getSaleOrderListing({ ...filters, merchantId }, currentPage, 20);
    setOrders(data.orders || []);
    setTotalPages(data.totalPages || 1);
    setTotalCount(data.totalCount || 0);
    setLoading(false);
  };

  useEffect(() => {
    fetchOrders();
  }, [selectedMerchant, filters, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedMerchant, filters]);

  useEffect(() => {
    if (filterModalOpen) setTempFilters(filters);
  }, [filterModalOpen, filters]);

  const applyFilters = () => {
    setFilters(tempFilters);
    setFilterModalOpen(false);
  };

  const clearFilters = () => {
    const cleared = { status: "", merchantSyncStatus: "", orderId: "", merchantOrderId: "", search: "", minPrice: "", maxPrice: "", startDate: "", endDate: "" };
    setTempFilters(cleared);
    setFilters(cleared);
    setFilterModalOpen(false);
  };

  const handleExpandClick = (orderId) => {
    const next = new Set(expandedRows);
    if (next.has(orderId)) next.delete(orderId);
    else next.add(orderId);
    setExpandedRows(next);
  };

  const handleStatusUpdate = async () => {
    if (!newStatus) return;
    await updateSaleOrderStatus(statusModal.orderId, newStatus);
    setStatusModal({ open: false, orderId: null, currentStatus: "" });
    setNewStatus("");
    fetchOrders();
  };

  const handleSyncTimeout = async () => {
    const merchantId = selectedMerchant && selectedMerchant !== "all" ? selectedMerchant : undefined;
    await syncTimeoutSaleOrders(merchantId);
    fetchOrders();
  };

  const renderAppliedFilters = () => {
    const active = Object.entries(filters).filter(([, v]) => v);
    if (active.length === 0) return null;
    return (
      <div className="mb-3">
        {active.map(([key, value]) => (
          <Badge key={key} color="dark" className="me-2 mb-2 p-2" style={{ backgroundColor: "#009689", fontSize: "0.8rem" }}>
            <strong>{key}:</strong> {value}
          </Badge>
        ))}
      </div>
    );
  };

  const renderPagination = () => {
    const pages = [];
    const maxShow = 5;
    const start = Math.max(1, currentPage - Math.floor(maxShow / 2));
    const end = Math.min(totalPages, start + maxShow - 1);
    if (start > 1) {
      pages.push(<PaginationItem key="first"><PaginationLink onClick={() => setCurrentPage(1)}>1</PaginationLink></PaginationItem>);
      if (start > 2) pages.push(<PaginationItem key="s-ell" disabled><PaginationLink>...</PaginationLink></PaginationItem>);
    }
    for (let i = start; i <= end; i++) {
      pages.push(<PaginationItem active={i === currentPage} key={i}><PaginationLink onClick={() => setCurrentPage(i)}>{i}</PaginationLink></PaginationItem>);
    }
    if (end < totalPages) {
      if (end < totalPages - 1) pages.push(<PaginationItem key="e-ell" disabled><PaginationLink>...</PaginationLink></PaginationItem>);
      pages.push(<PaginationItem key="last"><PaginationLink onClick={() => setCurrentPage(totalPages)}>{totalPages}</PaginationLink></PaginationItem>);
    }
    return pages;
  };

  const formatDate = (d) => {
    if (!d) return "-";
    return new Date(d).toLocaleString();
  };

  return (
    <Fragment>
      <Breadcrumb title="Sale Orders" parent="Orders" />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h5>Sale Orders ({totalCount})</h5>
                <div className="d-flex gap-2">
                  <Button color="warning" size="sm" onClick={handleSyncTimeout} title="Retry timeout orders">
                    <RefreshCw size={14} className="me-1" /> Retry Timeout
                  </Button>
                  <Button color="primary" onClick={() => setFilterModalOpen(true)}>Filters</Button>
                </div>
              </CardHeader>
              <CardBody>
                {renderAppliedFilters()}
                {loading ? (
                  <div className="text-center py-4">Loading...</div>
                ) : orders.length === 0 ? (
                  <div className="text-center text-muted py-4">No sale orders found.</div>
                ) : (
                  <>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead style={{ borderBottom: "2px solid #dee2e6" }}>
                        <tr>
                          <th style={thStyle}>Order #</th>
                          <th style={thStyle}>Customer</th>
                          <th style={thStyle}>Total</th>
                          <th style={thStyle}>Status</th>
                          <th style={thStyle}>Merchant Sync</th>
                          <th style={thStyle}>Merchant Order</th>
                          <th style={thStyle}>Date</th>
                          <th style={thStyle}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {orders.map((order) => (
                          <Fragment key={order.id}>
                            <tr
                              style={{ borderBottom: "1px solid #dee2e6", verticalAlign: "middle", cursor: "pointer" }}
                              onClick={() => handleExpandClick(order.id)}
                            >
                              <td style={tdStyle}>#{order.id}</td>
                              <td style={tdStyle}>
                                {order.User ? `${order.User.firstName} ${order.User.lastName}` : order.phone || "-"}
                              </td>
                              <td style={tdStyle}>{order.productCost?.toFixed(2)} SAR</td>
                              <td style={tdStyle}>
                                <Badge
                                  color={statusColors[order.status] || "secondary"}
                                  style={{ cursor: "pointer" }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setStatusModal({ open: true, orderId: order.id, currentStatus: order.status });
                                    setNewStatus(order.status);
                                  }}
                                >
                                  {order.status}
                                </Badge>
                              </td>
                              <td style={tdStyle}>
                                <Badge color={syncStatusColors[order.merchantSyncStatus] || "secondary"}>
                                  {order.merchantSyncStatus}
                                </Badge>
                                {order.syncRetryCount > 0 && (
                                  <small className="text-muted ms-1">({order.syncRetryCount}x)</small>
                                )}
                              </td>
                              <td style={tdStyle}>
                                {order.merchantOrderNo || <span className="text-muted">-</span>}
                              </td>
                              <td style={tdStyle}>
                                <small>{formatDate(order.createdAt)}</small>
                              </td>
                              <td style={tdStyle}>
                                <div style={{
                                  width: "32px", height: "32px", borderRadius: "50%",
                                  backgroundColor: "#fff", border: "1px solid #e6e6e6",
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                }}>
                                  {expandedRows.has(order.id) ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                </div>
                              </td>
                            </tr>
                            {expandedRows.has(order.id) && (
                              <tr>
                                <td colSpan="8" style={{ padding: 0 }}>
                                  <div style={{ padding: "20px", backgroundColor: "#f8f9fa", borderBottom: "1px solid #dee2e6" }}>
                                    <Row>
                                      <Col md="6" style={{ borderRight: "1px solid #dee2e6" }}>
                                        <h6 className="mb-3" style={{ color: "#009689", fontWeight: "bold" }}>Order Details</h6>
                                        <Row>
                                          <Col sm="6" className="mb-2">
                                            <div className="text-muted small">Phone</div>
                                            <div className="fw-bold">{order.phone || "N/A"}</div>
                                          </Col>
                                          <Col sm="6" className="mb-2">
                                            <div className="text-muted small">Payment</div>
                                            <div className="fw-bold">{order.payment}</div>
                                          </Col>
                                          <Col sm="6" className="mb-2">
                                            <div className="text-muted small">Payment Ref</div>
                                            <div className="fw-bold">{order.paymentReference || "N/A"}</div>
                                          </Col>
                                          <Col sm="6" className="mb-2">
                                            <div className="text-muted small">Discount</div>
                                            <div className="fw-bold">{order.discount || 0}</div>
                                          </Col>
                                          <Col sm="6" className="mb-2">
                                            <div className="text-muted small">Shipping Cost</div>
                                            <div className="fw-bold">{order.shippingCost || 0}</div>
                                          </Col>
                                          <Col sm="6" className="mb-2">
                                            <div className="text-muted small">Merchant</div>
                                            <div className="fw-bold">{order.Merchant?.name || "N/A"}</div>
                                          </Col>
                                          <Col sm="6" className="mb-2">
                                            <div className="text-muted small">Last Sync Attempt</div>
                                            <div className="fw-bold">{formatDate(order.lastSyncAttempt)}</div>
                                          </Col>
                                          {order.merchantSyncResponse && (
                                            <Col sm="12" className="mb-2">
                                              <div className="text-muted small">Merchant Response</div>
                                              <pre style={{ fontSize: "12px", background: "#fff", padding: "8px", borderRadius: "4px", maxHeight: "120px", overflow: "auto" }}>
                                                {order.merchantSyncResponse}
                                              </pre>
                                            </Col>
                                          )}
                                        </Row>
                                      </Col>
                                      <Col md="6" className="ps-4">
                                        <h6 className="mb-3" style={{ color: "#009689", fontWeight: "bold" }}>Items</h6>
                                        {order.SaleOrderItems?.length > 0 ? (
                                          <table style={{ width: "100%", fontSize: "13px" }}>
                                            <thead>
                                              <tr style={{ borderBottom: "1px solid #dee2e6" }}>
                                                <th style={{ padding: "6px" }}>Product</th>
                                                <th style={{ padding: "6px" }}>Qty</th>
                                                <th style={{ padding: "6px" }}>Price</th>
                                                <th style={{ padding: "6px" }}>Ext ID</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {order.SaleOrderItems.map((item) => (
                                                <tr key={item.id} style={{ borderBottom: "1px solid #f0f0f0" }}>
                                                  <td style={{ padding: "6px" }}>
                                                    {item.SystemProduct?.title || "-"}
                                                    {/* A box: what the customer put in it (sent to Odoo as these lines) */}
                                                    {Array.isArray(item.bundleSelections) && item.bundleSelections.length > 0 && (
                                                      <ul className="mb-0 mt-1 ps-3 text-muted" style={{ fontSize: "12px" }}>
                                                        {item.bundleSelections.map((s, i) => (
                                                          <li key={i}>
                                                            {s.itemTitle}
                                                            {s.quantity > 1 ? ` ×${s.quantity}` : ""}
                                                            {s.priceExtra > 0 ? ` (+${s.priceExtra})` : ""}
                                                          </li>
                                                        ))}
                                                      </ul>
                                                    )}
                                                  </td>
                                                  <td style={{ padding: "6px" }}>{item.quantity}</td>
                                                  <td style={{ padding: "6px" }}>{item.price?.toFixed(2)}</td>
                                                  <td style={{ padding: "6px" }}>
                                                    <small className="text-muted">{item.SystemProduct?.merchant_product_id || "-"}</small>
                                                  </td>
                                                </tr>
                                              ))}
                                            </tbody>
                                          </table>
                                        ) : (
                                          <p className="text-muted">No items.</p>
                                        )}
                                      </Col>
                                    </Row>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        ))}
                      </tbody>
                    </table>
                    {totalPages > 1 && (
                      <Pagination className="d-flex justify-content-end mt-3">
                        <PaginationItem disabled={currentPage <= 1}>
                          <PaginationLink previous onClick={() => setCurrentPage(currentPage - 1)} />
                        </PaginationItem>
                        {renderPagination()}
                        <PaginationItem disabled={currentPage >= totalPages}>
                          <PaginationLink next onClick={() => setCurrentPage(currentPage + 1)} />
                        </PaginationItem>
                      </Pagination>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Filter Modal */}
      <Modal isOpen={filterModalOpen} toggle={() => setFilterModalOpen(false)}>
        <ModalHeader toggle={() => setFilterModalOpen(false)}>Filter Sale Orders</ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label>Search (phone, merchant order, payment ref)</Label>
              <Input type="text" value={tempFilters.search} onChange={(e) => setTempFilters({ ...tempFilters, search: e.target.value })} />
            </FormGroup>
            <FormGroup>
              <Label>Order ID</Label>
              <Input type="text" value={tempFilters.orderId} onChange={(e) => setTempFilters({ ...tempFilters, orderId: e.target.value })} />
            </FormGroup>
            <FormGroup>
              <Label>Merchant Order ID</Label>
              <Input type="text" value={tempFilters.merchantOrderId} onChange={(e) => setTempFilters({ ...tempFilters, merchantOrderId: e.target.value })} />
            </FormGroup>
            <FormGroup>
              <Label>Order Status</Label>
              <Input type="select" value={tempFilters.status} onChange={(e) => setTempFilters({ ...tempFilters, status: e.target.value })}>
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="accepted">Accepted</option>
                <option value="packed">Packed</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>Merchant Sync Status</Label>
              <Input type="select" value={tempFilters.merchantSyncStatus} onChange={(e) => setTempFilters({ ...tempFilters, merchantSyncStatus: e.target.value })}>
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="synced">Synced</option>
                <option value="failed">Failed</option>
                <option value="timeout">Timeout</option>
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>Price Range</Label>
              <div className="d-flex gap-2">
                <Input type="number" placeholder="Min" value={tempFilters.minPrice} onChange={(e) => setTempFilters({ ...tempFilters, minPrice: e.target.value })} />
                <Input type="number" placeholder="Max" value={tempFilters.maxPrice} onChange={(e) => setTempFilters({ ...tempFilters, maxPrice: e.target.value })} />
              </div>
            </FormGroup>
            <FormGroup>
              <Label>Date Range</Label>
              <div className="d-flex gap-2">
                <Input type="date" value={tempFilters.startDate} onChange={(e) => setTempFilters({ ...tempFilters, startDate: e.target.value })} />
                <Input type="date" value={tempFilters.endDate} onChange={(e) => setTempFilters({ ...tempFilters, endDate: e.target.value })} />
              </div>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={applyFilters}>Apply</Button>
          <Button color="secondary" onClick={clearFilters}>Clear</Button>
        </ModalFooter>
      </Modal>

      {/* Status Update Modal */}
      <Modal isOpen={statusModal.open} toggle={() => setStatusModal({ open: false, orderId: null, currentStatus: "" })}>
        <ModalHeader toggle={() => setStatusModal({ open: false, orderId: null, currentStatus: "" })}>
          Update Order Status
        </ModalHeader>
        <ModalBody>
          <FormGroup>
            <Label>New Status</Label>
            <Input type="select" value={newStatus} onChange={(e) => setNewStatus(e.target.value)}>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="packed">Packed</option>
              <option value="shipped">Shipped</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </Input>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={handleStatusUpdate}>Update</Button>
          <Button color="secondary" onClick={() => setStatusModal({ open: false, orderId: null, currentStatus: "" })}>Cancel</Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

const thStyle = {
  padding: "10px",
  textAlign: "left",
  background: "#f2f2f2",
  borderBottom: "1px solid #dee2e6",
};

const tdStyle = {
  padding: "10px",
  textAlign: "left",
};

export default SaleOrderList;
